import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { ProjectSubmissionToken, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
	Badge,
	Button,
	Card,
	Group,
	Loader,
	ScrollArea,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip
} from "@mantine/core";
import {
	IconAlertCircle,
	IconSearch
} from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";
import SubmissionTokensBrowserModal from "../modals/SubmissionTokensBrowserModal";

const UsersBrowserTable: React.FC = () => {
	const [users, setUsers] = useState<User[] | null | undefined>(undefined);
	const [userToEdit, setUserToEdit] = useState<User | null>(null);
	const [showSubmissionTokenBrowserModal, setShowSubmissionTokenBrowserModal] = useState(false);

	// NEW: name filter state
	const [nameQuery, setNameQuery] = useState<string>("");

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("UsersBrowser must be a child of SessionContext.");
	}
	const token = session.token;
	if (!token) return null;

	const fetchUsers = () => {
		setUsers(undefined);

		ConductionAPI.getAllUsers(token)
			.then((loadedUsersInfo: User[] | null) => {
				if (!loadedUsersInfo || loadedUsersInfo.length === 0) {
					setUsers(null);
				} else {
					setUsers(
						loadedUsersInfo.sort(
							(a, b) => b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
						)
					);
					if (userToEdit != null) {
						const updatedUser = loadedUsersInfo.find((u) => u.id === userToEdit.id) ?? null;
						setUserToEdit(updatedUser);
					}
				}
			})
			.catch(() => {
				Utils.displayNotification(
					"Error Fetching Users",
					"An error occurred while retrieving users.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
				setUsers(null);
			});
	};

	useEffect(() => {
		fetchUsers();
	}, [token]);

	const filteredUsers = useMemo(() => {
		if (!Array.isArray(users)) return users;
		const q = nameQuery.trim().toLowerCase();
		if (!q) return users;
		return users.filter((u) => {
			const username = (u.username ?? "").toLowerCase();
			const firstName = (u.firstName ?? "").toLowerCase();
			const lastName = (u.lastName ?? "").toLowerCase();
			return username.includes(q) || firstName.includes(q) || lastName.includes(q);
		});
	}, [users, nameQuery]);

	return (
		<Card h="80vh" w="90%">
			{/* Modals */}
			<SubmissionTokensBrowserModal
				user={userToEdit}
				showModal={showSubmissionTokenBrowserModal}
				setShowModal={(show) => {
					if (!show) {
						setUserToEdit(null);
						setShowSubmissionTokenBrowserModal(false);
					}
				}}
				onChange={fetchUsers}
			/>

			<Group justify="space-between" mb="md">
				<Title>Users</Title>
			</Group>

			<ScrollArea style={{ width: "100%", minHeight: "100%" }}>
				{users === undefined ? (
					<Loader height="100%" width="100%" radius="md" />
				) : users === null ? (
					<Text>No users found.</Text>
				) : (
					<Table striped stickyHeader highlightOnHover withColumnBorders>
						<Table.Thead>
							<Table.Tr>
								<Table.Th>
									<div style={{ display: "grid", gap: 6, minWidth: 260 }}>
										<Text size="sm" fw={600}>
											Username
										</Text>
										<TextInput
											size="xs"
											placeholder="Search name..."
											value={nameQuery}
											onChange={(e) => setNameQuery(e.currentTarget.value)}
											leftSection={<IconSearch size={14} />}
											leftSectionPointerEvents="none"
											autoComplete="off"
										/>
									</div>
								</Table.Th>
								<Table.Th>First Name</Table.Th>
								<Table.Th>Last Name</Table.Th>
								<Table.Th>Email</Table.Th>
								<Table.Th>Created</Table.Th>
								<Table.Th>Projects</Table.Th>
								<Table.Th>Credits</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{filteredUsers!.map((user) => {
								const usedActiveCredits =
									user.tokens?.reduce(
										(acc: number, token: ProjectSubmissionToken) => acc + (token.isActive() ? token.submissions.length : 0),
										0
									) ?? 0;
								const totalActiveCredits =
									user.tokens?.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getActiveCredits(), 0) ??
									0;
								return (
									<Table.Tr key={user.id}>
										<Table.Td><Text>{user.username}</Text></Table.Td>
										<Table.Td><Text>{user.firstName ?? ""}</Text></Table.Td>
										<Table.Td><Text>{user.lastName ?? ""}</Text></Table.Td>
										<Table.Td><Text>{user.email ?? ""}</Text></Table.Td>
										<Table.Td>
											<Badge color="blue">{formatDateString(user.dateCreated)}</Badge>
										</Table.Td>
										<Table.Td>
											<Text>{user.projectIds.length}</Text>
										</Table.Td>
										<Table.Td>
											<Group>
												<Text>{`${usedActiveCredits} / `}</Text>
												<Tooltip label={`Edit submission credit tokens for ${user.username}`} withArrow>
													<Button
														variant="subtle"
														size="xs"
														onClick={() => { setUserToEdit(user); setShowSubmissionTokenBrowserModal(true); }}
													>
														{totalActiveCredits}
													</Button>
												</Tooltip>
											</Group>
										</Table.Td>
									</Table.Tr>
								);
							})}
						</Table.Tbody>
					</Table>
				)}
			</ScrollArea>
		</Card>
	);
};

function formatDateString(stringDate: Date): string {
	if (!stringDate) return "Invalid Date";
	const date = new Date((stringDate as unknown as string) + " UTC");
	return date.toLocaleDateString();
}

export default UsersBrowserTable;
